import { createSlice } from '@reduxjs/toolkit'

import { LocalStorageUtility } from '@Utils/storage/StorageUtility'

import { useAppDispatch, useAppSelector } from '../hooks'

const SIDEBAR_STORAGE_KEY = 'sidebarExpanded'

/**
 * The user's explicit collapse/expand choice, persisted across reloads.
 * Returns null when the user has never made a choice, so callers can fall back
 * to a sensible default (e.g. expanded once onboarding is complete).
 */
export const readStoredSidebarExpanded = (): boolean | null => {
  const stored = new LocalStorageUtility().getSafe(SIDEBAR_STORAGE_KEY)?.value
  return typeof stored === 'boolean' ? stored : null
}

const persistSidebarExpanded = (value: boolean): void => {
  new LocalStorageUtility().save(SIDEBAR_STORAGE_KEY, value)
}

const INITIAL_LAYOUT_STATE = {
  sidebarExpanded: readStoredSidebarExpanded() ?? false,
}

const layoutSlice = createSlice({
  name: 'layout',
  initialState: INITIAL_LAYOUT_STATE,
  reducers: {
    // Programmatic set (e.g. deriving from onboarding status). Intentionally
    // does NOT persist — only explicit user actions below record a preference.
    setSidebarExpanded(state, action) {
      state.sidebarExpanded = action.payload as boolean
    },
    expandSidebar(state) {
      state.sidebarExpanded = true
      persistSidebarExpanded(true)
    },
    collapseSidebar(state) {
      state.sidebarExpanded = false
      persistSidebarExpanded(false)
    },
    toggleSidebar(state) {
      state.sidebarExpanded = !state.sidebarExpanded
      persistSidebarExpanded(state.sidebarExpanded)
    },
  },
})

const { actions, reducer } = layoutSlice

export default reducer

export const {
  setSidebarExpanded,
  expandSidebar,
  collapseSidebar,
  toggleSidebar,
} = actions

export const useLayoutState = (): typeof INITIAL_LAYOUT_STATE =>
  useAppSelector((state) => state.layout)

export const useLayoutActions = () => {
  const dispatch = useAppDispatch()

  return {
    setSidebarExpanded: (payload: boolean) =>
      dispatch(setSidebarExpanded(payload)),
    expandSidebar: () => dispatch(expandSidebar()),
    collapseSidebar: () => dispatch(collapseSidebar()),
    toggleSidebar: () => dispatch(toggleSidebar()),
  }
}
